<template>
  <div class="mobile-login-qr text-center">
    <!-- Loading: minting the QR -->
    <div v-if="phase === 'loading'" class="qr-placeholder">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error / expired -->
    <div v-else-if="phase === 'error' || phase === 'expired'" class="qr-error">
      <p class="qr-error__msg">
        {{ phase === 'expired' ? 'This code expired. Generate a new one.' : (errorMessage || 'Something went wrong.') }}
      </p>
      <button type="button" class="qr-cta" @click="loadLink">Generate a new code</button>
    </div>

    <!-- Step 1 — QR view (no code shown here) -->
    <template v-else-if="phase === 'qr'">
      <!-- On a phone browser you can't scan a QR shown on the same phone, so offer a
           tap-to-open button that deep-links into the app instead. -->
      <template v-if="onMobile">
        <p class="mb-3">Open this in the HiiKER app to sign in on this phone. No log in needed.</p>
        <button type="button" class="qr-cta" @click="openAppLink">Open in the HiiKER app</button>
        <p class="text-muted mt-3 mb-0"><small>Waiting for you to continue… · expires in {{ countdown }}</small></p>
      </template>
      <template v-else>
        <p class="mb-3">Scan the QR code with your phone camera to open your profile on the HiiKER app. No log in needed.</p>
        <div class="qr-container card p-2 mx-auto" style="max-width: 320px;">
          <img class="img-fluid" :src="qrDataUri" alt="QR code to install HiiKER and sign in" />
        </div>
        <p class="text-muted mt-3 mb-0"><small>Waiting for you to scan… · expires in {{ countdown }}</small></p>
      </template>
    </template>

    <!-- Step 2 — Code view (shown once the phone has reported a scan) -->
    <template v-else-if="phase === 'code'">
      <p class="mb-2">Enter the 6 digit code in the app when prompted.</p>
      <div class="pairing-code" aria-label="Login code">{{ code }}</div>
      <p class="text-muted mt-3 mb-0"><small>expires in {{ countdown }}</small></p>
    </template>

    <!-- Step 3 — Success view -->
    <template v-else-if="phase === 'success'">
      <div class="success-view">
        <div class="success-check" aria-hidden="true">✓</div>
        <p class="mt-3 mb-0">You have signed into the app! Enjoy!</p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { isMobileDevice } from '../../utils/isMobileDevice';

// phase drives which view shows. It maps the server pairing status
// (pending → scanned → redeemed) plus local loading/error/expired states.
type Phase = 'loading' | 'qr' | 'code' | 'success' | 'expired' | 'error';

const props = withDefaults(defineProps<{ autoStart?: boolean }>(), { autoStart: true });
// phaseChange lets a host (e.g. the modal wrapper) react to the FSM without owning it.
const emit = defineEmits<{ (e: 'success'): void; (e: 'phaseChange', phase: Phase): void }>();

const phase = ref<Phase>('loading');
const errorMessage = ref<string | null>(null);
// The QR is rendered server-side (rqrcode) and returned as a base64 SVG data URI —
// the sign-in short URL is a credential and must never be sent to a third party.
const qrDataUri = ref<string | null>(null);
// The LinkForty deferred deep link the QR encodes. On phone browsers we surface it as a
// tap-to-open button (you can't scan a QR shown on the same phone); on desktop it's unused.
const shortUrl = ref<string | null>(null);
// Decided once on setup — the UA doesn't change mid-session.
const onMobile = isMobileDevice();
// Pairing token for THIS QR; used to poll status. Already in the QR on screen.
const token = ref<string | null>(null);
// The 6-digit code, revealed once the server reports status === 'scanned'.
const code = ref<string | null>(null);
const expiresAt = ref<Date | null>(null);
const nowTick = ref(Date.now());
let tickInterval: number | null = null;
// Self-scheduling poll: we wait POLL_MS *between* requests rather than firing every
// POLL_MS, so a slow/overlapping request can't stack up or clobber newer state.
let pollTimer: number | null = null;
let polling = false;

const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 10000; // abort a hung status request so the chain never stalls

watch(phase, (p) => emit('phaseChange', p));

const countdown = computed(() => {
  if (!expiresAt.value) return '';
  const msRemaining = expiresAt.value.getTime() - nowTick.value;
  if (msRemaining <= 0) return 'expired';
  const totalMinutes = Math.floor(msRemaining / 60000);
  return totalMinutes > 0 ? `${totalMinutes}m` : `${Math.ceil(msRemaining / 1000)}s`;
});

function stopPolling() {
  polling = false;
  if (pollTimer !== null) {
    window.clearTimeout(pollTimer);
    pollTimer = null;
  }
}

// Opens the deferred deep link in a new tab. Mobile-only path; mirrors the
// window.open(url, '_blank', 'noopener') pattern used elsewhere (OpenOnGoogleMaps.ts).
function openAppLink() {
  if (shortUrl.value) window.open(shortUrl.value, '_blank', 'noopener');
}

function resetPairing() {
  stopPolling();
  qrDataUri.value = null;
  shortUrl.value = null;
  token.value = null;
  code.value = null;
  errorMessage.value = null;
}

async function loadLink() {
  phase.value = 'loading';
  resetPairing();
  try {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const response = await fetch('/api/v1/mobile-login-link', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Unable to generate sign-in code');
    }

    const body = await response.json();
    qrDataUri.value = body.qr_data_uri;
    shortUrl.value = body.short_url;
    token.value = body.token;
    expiresAt.value = body.expires_at ? new Date(body.expires_at) : null;
    phase.value = 'qr';
    startPolling();
  } catch (e: any) {
    console.error('[MobileLoginQR]', e);
    errorMessage.value = e.message || 'Something went wrong';
    phase.value = 'error';
  }
}

function startPolling() {
  stopPolling();
  polling = true;
  // Run a poll, wait for it to finish, THEN schedule the next — never overlapping.
  const tick = async () => {
    await pollStatus();
    if (polling) pollTimer = window.setTimeout(tick, POLL_MS);
  };
  tick();
}

// Detects the two server-side events: the phone scanning (pending → scanned) and a
// successful login (scanned → redeemed). The same poll drives all view transitions.
async function pollStatus() {
  if (!token.value) return;

  // Local expiry guard so we stop even if the status call is slow.
  if (expiresAt.value && expiresAt.value.getTime() - Date.now() <= 0) {
    phase.value = 'expired';
    stopPolling();
    return;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
  try {
    const response = await fetch(
      `/api/v1/mobile-login/status?token=${encodeURIComponent(token.value)}`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal }
    );

    if (response.status === 404) {
      // Pairing gone (expired / consumed past its success window).
      phase.value = 'expired';
      stopPolling();
      return;
    }
    if (!response.ok) return; // transient — keep polling

    const body = await response.json();
    switch (body.status) {
      case 'scanned':
        code.value = body.code;
        phase.value = 'code';
        break;
      case 'redeemed':
        phase.value = 'success';
        stopPolling();
        emit('success');
        break;
      case 'pending':
      default:
        phase.value = 'qr';
    }
  } catch (e) {
    // Network blip or aborted timeout — keep polling; the expiry guard ends it.
    console.error('[MobileLoginQR] status poll failed', e);
  } finally {
    window.clearTimeout(timeout);
  }
}

// Host wrappers (e.g. the modal) can drive the lifecycle when autoStart is false.
defineExpose({ start: loadLink, reset: resetPairing });

onMounted(() => {
  tickInterval = window.setInterval(() => { nowTick.value = Date.now(); }, 1000);
  if (props.autoStart) loadLink();
});

onUnmounted(() => {
  if (tickInterval !== null) window.clearInterval(tickInterval);
  stopPolling();
});
</script>

<style scoped lang="scss">
.qr-placeholder {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Dark, high-contrast error/expired panel — readable on the dark modal/welcome/onboarding
// surfaces (the old Bootstrap .alert-warning was a light cream box that clashed).
.qr-error {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 1.75rem 1.25rem;
}

.qr-error__msg {
  color: #fff;
  font-weight: 600;
  margin: 0 0 1rem;
}

// Shared yellow CTA — used by the error/expired "Generate a new code" button and the
// mobile "Open in the HiiKER app" button.
.qr-cta {
  background-color: var(--yellow, #fddf00);
  color: #1a1a1a;
  border: 0;
  border-radius: 8px;
  padding: 0.5rem 1.5rem;
  font-weight: 700;
  cursor: pointer;
}

.qr-cta:hover {
  filter: brightness(1.05);
}

.pairing-code {
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 0.6rem;
  text-indent: 0.6rem;
  font-variant-numeric: tabular-nums;
}

.success-view {
  // Vertical breathing room so the success state isn't a short, wide strip.
  padding: 2rem 0 2.5rem;
}

.success-check {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  background-color: #2e7d32;
  color: white;
  font-size: 3.75rem;
  line-height: 120px;
}
</style>
